import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { AuthApi } from '../apis/auth-api';
import { UserApi } from '../apis/user-api';
import { CustomerServiceApi } from '../apis/customer-service-api';
import CloseableErrorMessage from '../components/CloseableErrorMessage';
import { GenerateCSTokenRequest } from '../models/generate-cs-token-request-interface';
import { UserCompanyActiveStatus } from '../models/user-profile-inteface';
import { CSUserSecurityToken } from '../models/cs-user-security-token-interface';
import { ActivityLogApi } from '../apis/activity-log-api';
import { UserExportOptions } from '../models/user-export-options-interface';

export const UserQueries = {
   useCompanyUsersSimple: (companyId: number | undefined, deleted: boolean = true) =>
    useQuery({
      queryKey: ['companyUsersSimple', companyId],
      queryFn: async () => await UserApi.getUsers(companyId!),
      enabled: !!companyId && !deleted,
      onError: () => CloseableErrorMessage('Error getting users.'),
    }),

  useUserCompanyProfiles: (userId: number) =>
    useQuery({
      queryKey: ['userCompanyProfiles', userId],
      queryFn: async () => await UserApi.getUserCompanyProfiles(userId),
      enabled: !!userId,
      refetchOnWindowFocus: false
    }),

  useCustomerServiceToken: (csAccess: boolean, userId: number) =>
    useQuery({
      queryKey: ['customerServiceToken', csAccess, userId],
      queryFn: async () => await AuthApi.getCustomerServiceToken(userId),
      enabled: !!csAccess && !!userId,
      onError: () => CloseableErrorMessage('Error getting customer service token.'),
      refetchOnWindowFocus: false
    }),

  useUserActivity: (userId: number | undefined) =>
    useQuery({
      queryKey: ['userActivity', userId],
      queryFn: async () => await ActivityLogApi.getUserActivity(userId!),
      enabled: !!userId,
      onError: () => CloseableErrorMessage('Error getting user activity.')
    }),

  useTwoFactorDeliveryHistory: (userId: number | undefined) =>
    useQuery({
      queryKey: ['twoFactorDeliveryHistory', userId],
      queryFn: async () => await CustomerServiceApi.getTwoFactorDeliveryHistory(userId!),
      enabled: !!userId,
      onError: () => CloseableErrorMessage('Error getting two factor delivery history.'),
      refetchOnWindowFocus: false
    }),

  useResetPassword: (userName: string) => {
    return useMutation({
      mutationFn: async (data: {userName: string, applicationId: number, plain: boolean}) => await AuthApi.getCSPasswordReset(data.userName, data.applicationId, data.plain),
      onError: () => CloseableErrorMessage('Error resetting password.'),
      onSuccess: () => {
        message.success('Password reset email sent to user.');
      }
    })
  },

  usePutUserActive: (userId: number) => {
    return useMutation({
      mutationFn: async (data: UserCompanyActiveStatus) => await UserApi.putUserActive(data.companyId, userId, data.active),
      onError: () => CloseableErrorMessage('Error updating user active status.'),
      onSuccess: () => {
        message.success('Updated user company active status successfully.');
      }
    })   
  },
  
  usePasswordResetLink: (userName: string) => {
    return useMutation({
      mutationFn: async (data: {userName: string, applicationId: number}) => await AuthApi.getPasswordResetLink(data.userName, data.applicationId),
      onError: () => CloseableErrorMessage('Error getting password reset link.')
    })
  },

  useGrantAccess: (userId: number) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (data: GenerateCSTokenRequest) : Promise<CSUserSecurityToken> => await AuthApi.postGenerateAccessToken(data),
      onError: () => CloseableErrorMessage('Error granting access.'),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['userCompanyProfiles', userId] });
        message.success('Access granted successfully.');
      }
    })
  },

  useRevokeAccess: (userId: number) => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (data: { userId: number, securityTokenTypeId: number }) => await AuthApi.postRevokeUserSecurityToken(data),
      onError: () => CloseableErrorMessage('Error revoking access.'),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['userCompanyProfiles', userId] });
        message.success('Access revoked successfully.');
      }
    })
  },

  useUsersExport: () => {
    return useMutation({
      mutationFn: async (data: UserExportOptions) => await UserApi.postCSUsersExport(data),
      onError: () => CloseableErrorMessage('Error exporting users.'),
      onSuccess: () => {
        message.success('Users exported successfully.');
      }
    })
  },

  useUnlockAccount: () => {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: async (data: { username: string, userId: number }) => await AuthApi.postAccountUnlock(data.username),
      onError: () => CloseableErrorMessage('Error unlocking account.'),
      onSuccess: (_, { userId }) => {
        queryClient.invalidateQueries({ queryKey: ['userCompanyProfiles', userId] });
        message.success('Account unlocked successfully.');
      }
    })
  },

  useOnboardUserExport: (companyId: number | undefined) =>
    useMutation({
      mutationFn: async () => await UserApi.getOnboardCSUsersExport(companyId!),
      onError: () => CloseableErrorMessage('Error generating user export file.'),
      onSuccess: (results) => {
        const resultBytes = Uint8Array.from(atob(results), c => c.charCodeAt(0))
        const blob = new Blob([resultBytes], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `onboard_govenda_user_export_${companyId}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    }),
};
